/**
 * AssetPicker — compact dropdown for switching the active asset on any
 * portfolio-scoped screen.
 *
 * Uses a Button trigger + popover Menu (instead of a bare <Select>) so it
 * matches the Aurora outline-button aesthetic used by the rest of the chrome.
 *
 * Props:
 *   value       : 'all' | <duid>
 *   onChange    : (newValue) => void
 *   assets      : list of {asset_id, name, region, fuel}
 *   colors      : theme palette from useThemeColors()
 *   allowAll    : whether to include an "All assets" option (default true)
 *   placeholder : text shown when value is falsy (e.g. while initial fetch
 *                 is in flight) (default 'Select asset')
 *   minWidth    : optional override for trigger width (default 220)
 */

import React, { useState } from 'react';
import {
  Box, Typography, Button,
  Menu, MenuItem, ListItemText, Divider,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';

const FUEL_COLOR = {
  Solar:   '#f59e0b',
  Wind:    '#3b82f6',
  Battery: '#8b5cf6',
  Other:   '#6b7280',
};

export default function AssetPicker({
  value, onChange, assets, colors,
  allowAll = true, placeholder = 'Select asset', minWidth = 220,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const selected = value && value !== 'all'
    ? (assets || []).find((a) => a.asset_id === value)
    : null;

  const triggerLabel = value === 'all'
    ? 'All assets'
    : (selected?.name || placeholder);

  const triggerSecondary = value === 'all'
    ? `${(assets || []).length} ${(assets || []).length === 1 ? 'asset' : 'assets'}`
    : (selected ? `${selected.region} · ${selected.fuel}` : '');

  const handlePick = (v) => {
    setAnchorEl(null);
    onChange(v);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<ViewModuleOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
        endIcon={<ExpandMoreIcon sx={{
          fontSize: 18,
          transition: 'transform 160ms ease',
          transform: open ? 'rotate(180deg)' : 'none',
        }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          color: 'text.primary',
          borderColor: colors.hairline2,
          bgcolor: 'transparent',
          py: 0.55, px: 1.25,
          minWidth,
          justifyContent: 'space-between',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: colors.hover,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>
            {triggerLabel}
          </Typography>
          {triggerSecondary && (
            <Typography sx={{
              fontSize: '0.66rem', color: 'text.disabled',
              lineHeight: 1.1, letterSpacing: '0.02em',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}>
              {triggerSecondary}
            </Typography>
          )}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5, minWidth: Math.max(minWidth, 260),
            border: '1px solid', borderColor: colors.hairline2,
            borderRadius: 1.5,
            bgcolor: colors.surface,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          },
        }}
        MenuListProps={{ sx: { py: 0.5 } }}
      >
        {allowAll && (
          <MenuItem
            selected={value === 'all'}
            onClick={() => handlePick('all')}
            sx={{
              py: 0.9, px: 1.5,
              '&.Mui-selected': { bgcolor: colors.hover },
              '&.Mui-selected:hover': { bgcolor: colors.hover },
            }}
          >
            <ListItemText
              primary="All assets"
              secondary={`Portfolio aggregate · ${(assets || []).length} ${(assets || []).length === 1 ? 'asset' : 'assets'}`}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.7rem', color: 'text.disabled' }}
            />
            {value === 'all' && <CheckIcon sx={{ fontSize: 16, color: 'primary.main', ml: 1 }} />}
          </MenuItem>
        )}
        {allowAll && <Divider sx={{ borderColor: colors.hairline, my: 0.25 }} />}
        {(assets || []).map((a) => (
          <MenuItem
            key={a.asset_id}
            selected={a.asset_id === value}
            onClick={() => handlePick(a.asset_id)}
            sx={{
              py: 0.9, px: 1.5,
              '&.Mui-selected': { bgcolor: colors.hover },
              '&.Mui-selected:hover': { bgcolor: colors.hover },
            }}
          >
            <FiberManualRecordIcon sx={{ fontSize: 9, mr: 1.25, color: FUEL_COLOR[a.fuel] || FUEL_COLOR.Other }} />
            <ListItemText
              primary={a.name}
              secondary={`${a.asset_id} · ${a.region} · ${a.fuel}`}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.7rem', color: 'text.disabled' }}
            />
            {a.asset_id === value && <CheckIcon sx={{ fontSize: 16, color: 'primary.main', ml: 1 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// Used by both AssetPicker (internally) and Stack import in this file.
// Re-exported in case consumers want to align fuel-coloured dots elsewhere.
export { FUEL_COLOR };
